import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { 
  AlertTriangle, 
  Zap, 
  Plus,
  ArrowUpCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';

const PLAN_DETAILS = {
  SMALL_BUSINESS: { 
    name: 'Small Business', 
    price: '$250/mo', 
    pursuits: 2,
    nextTier: 'GROWTH',
    nextTierName: 'Growth',
    nextTierPrice: '$950/mo',
    nextTierPursuits: 10
  },
  GROWTH: { 
    name: 'Growth', 
    price: '$950/mo', 
    pursuits: 10,
    nextTier: 'ENTERPRISE',
    nextTierName: 'Enterprise',
    nextTierPrice: 'Custom',
    nextTierPursuits: 'Unlimited'
  },
  ENTERPRISE: { 
    name: 'Enterprise', 
    price: 'Custom', 
    pursuits: 'Unlimited',
    nextTier: null,
    nextTierName: null,
    nextTierPrice: null,
    nextTierPursuits: null
  }
};

export default function PursuitLimitModal({ 
  isOpen, 
  onClose, 
  limitInfo,
  onRequestSlot,
  onRequestUpgrade
}) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(null);

  const plan = limitInfo?.plan || 'SMALL_BUSINESS';
  const planDetails = PLAN_DETAILS[plan] || PLAN_DETAILS.SMALL_BUSINESS;
  const slotPrice = limitInfo?.slot_price || 150;

  const handleSlotRequest = async () => {
    setIsRequesting(true);
    try {
      await onRequestSlot?.();
      setRequestSent('slot');
    } catch (err) {
      console.error('Slot request failed:', err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleUpgradeRequest = async () => {
    setIsRequesting(true);
    try {
      await onRequestUpgrade?.();
      setRequestSent('upgrade');
    } catch (err) {
      console.error('Upgrade request failed:', err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleClose = () => {
    setRequestSent(null);
    onClose();
  };

  // Success state after request
  if (requestSent) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-testid="limit-request-success-modal">
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Request Submitted!
            </h3>
            <p className="text-slate-600 mb-6">
              {requestSent === 'slot' 
                ? 'Your pursuit slot request has been sent to the administrator. You will be notified once it is processed.'
                : 'Your plan upgrade request has been submitted. Our team will contact you within 24 hours.'}
            </p>
            <Button onClick={handleClose} className="bg-teal-700 hover:bg-teal-800">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="pursuit-limit-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Pursuit Limit Reached
              </DialogTitle>
              <DialogDescription>
                You've used all your available pursuit slots
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Current Usage */}
        <div className="bg-slate-50 rounded-lg p-4 my-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-slate-500">Current Usage</span>
            <Badge className="bg-slate-200 text-slate-700">
              {planDetails.name} Plan
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold text-slate-900">{limitInfo?.current_count || 0}</p>
              <p className="text-xs text-slate-500">Active Pursuits</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{limitInfo?.base_limit || planDetails.pursuits}</p>
              <p className="text-xs text-slate-500">Plan Limit</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-teal-600">{limitInfo?.purchased_slots || 0}</p>
              <p className="text-xs text-slate-500">Purchased</p>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {/* Option A: Upgrade Plan */}
          {planDetails.nextTier && (
            <div 
              className="border-2 border-purple-200 bg-purple-50/50 rounded-lg p-4 cursor-pointer hover:border-purple-400 transition-colors"
              onClick={handleUpgradeRequest}
              data-testid="upgrade-plan-option"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ArrowUpCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-900">Option A: Upgrade Plan</h4>
                    <Badge className="bg-purple-100 text-purple-700">Recommended</Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    Upgrade to <span className="font-medium">{planDetails.nextTierName}</span> for{' '}
                    <span className="font-medium">{planDetails.nextTierPursuits}</span> pursuits
                  </p>
                  <p className="text-lg font-bold text-purple-700 mt-2">
                    {planDetails.nextTierPrice}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Option B: Buy Slot */}
          <div 
            className="border-2 border-teal-200 bg-teal-50/50 rounded-lg p-4 cursor-pointer hover:border-teal-400 transition-colors"
            onClick={handleSlotRequest}
            data-testid="buy-slot-option"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Plus className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-900">Option B: Add Pursuit Slot</h4>
                  <Badge className="bg-teal-100 text-teal-700">One-Time</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Purchase 1 additional pursuit slot (permanent)
                </p>
                <p className="text-lg font-bold text-teal-700 mt-2">
                  ${slotPrice.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-start gap-2">
            <Zap className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-500">
              Purchased slots are <span className="font-medium">permanent</span>. When you archive or complete a pursuit, 
              your slot becomes available for a new pursuit.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
